// CORE
import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useToggle } from 'react-use';

// APP-COMPONENTS
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsActions, accountsSelectors, accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsActions, institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getLogger, tracker } from 'app-components/utils/core';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import LockIcon from '@mui/icons-material/LockRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ErrorIcon from '@mui/icons-material/WarningRounded';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import {
  type FundingAccount,
  FundingAccountOwnerTypeEnum,
  type FundingAccountType,
  mkFundingAccount,
  statusEnum,
  subStatusEnum,
} from 'data/fundingAccounts/types';
import * as fundingAccountUtils from 'data/fundingAccounts/utils';

// COMPONENTS
import InstitutionErrorBanner from 'components/AccountCard/InstitutionErrorBanner';
import type { OnboardAccountsForTransfersDialogProps } from 'components/Dialogs/A2A/actions';
import {
  showAccountLockoutDialog,
  showInitiateTransferDialog,
  showMicrodespositDialog,
  showOnboardAccountsDialog,
} from 'components/Dialogs/A2A/actions';
import SelectAccountForm from 'components/Dialogs/A2A/components/SelectAccountForm';
import TransferAccountRow from 'components/Dialogs/A2A/components/TransferAccountRow';
import TransferInstitutionRow from 'components/Dialogs/A2A/components/TransferInstitutionRow';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import SetupPage from 'components/Dialogs/IdentityVerification/SetupPage';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import InstitutionIcon from 'components/InstitutionIcon';
import LoadingView from 'components/LoadingView';
import QButton from 'components/QButton';

import { Button } from '@mui/material';
import QIllustration from 'components/QIllustration';
import ZeroStateView from 'components/ZeroStateView';
// LOCAL
import { type onboardA2ADialogIndex, onboardA2ADialogIndexEnums } from './onboardA2ADialogIndexEnums';

const log = getLogger('components/Dialogs/A2A/OnboardAccountsForTransfers');

const useStyles = makeStyles()((theme) => ({
  errorTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  viewOneWrapper: {
    overflowX: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    overflowY: 'auto',
  },
  institutionSelectWrapper: {
    paddingRight: 20,
    width: '100%',
    marginLeft: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  instituteIconWrapper: {
    minWidth: 64,
  },
  accountFormWrapper: {
    marginTop: 20,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  moneyColumn: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  accountEligibleTitle: {
    marginTop: 8,
  },
  accountEligibleText: {
    marginTop: 20,
  },
  accountDetailText: {
    marginBottom: 10,
  },
  accountEligibleNumber: {
    marginBottom: 10,
    color: theme.palette.text.secondary,
  },
  secondaryTextItem: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
    width: 600,
    marginLeft: 10,
  },
  buttonWrapper: {
    paddingBottom: 20,
  },
  endButton: {
    marginTop: theme.spacing(2),
    float: 'left',
  },
  errorBanner: {
    margin: '0px 0px 0px 0px',
  },
  pendingTitle: {
    marginBottom: 16,
  },
  marginBottom: {
    marginBottom: theme.spacing(1),
  },
  marginBottomTwo: {
    marginBottom: theme.spacing(2),
  },
  transferAccountRow: {
    marginBottom: 0,
  },
  successTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  secondaryText: {
    color: theme.palette.text.secondary,
  },
  bold: {
    fontWeight: 500,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  warningIcon: {
    color: theme.palette.warning.main,
    marginRight: 8,
  },
  subLock: {
    height: 16,
    width: 16,
    marginRight: 8,
    color: theme.palette.icon.light,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  loadingSubText: {
    marginTop: theme.spacing(1),
  },
  errorTextWrap: {
    backgroundColor: theme.palette.warning.light,
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 12,
    padding: '8px 16px 8px 16px',
  },
  errorIcon: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(1),
  },
  accountWrapper: {
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    marginTop: 10,
    marginBottom: 20,
  },
  divider: {
    marginTop: 15,
    marginBottom: 15,
  },
  insideWrapper: {
    width: 350,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: theme.spacing(1),
    marginBottom: 30,
  },
  accountNumberWrapper: {
    marginTop: 10,
    marginBottom: 20,
    marginRight: 20,
  },
  detailsWrapper: {
    marginTop: 5,
    marginLeft: 10,
  },
}));

//  ====================== VALIDATION FUNCTIONS ===========================

//  Ensure that the routing number is legitimate
const validateRoutingNumber = (routingNumber: string) => {
  const digits = routingNumber?.split('')?.map((d) => parseInt(d, 10));
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    (digits[2] + digits[5] + digits[8]);

  return checksum !== 0 && checksum % 10 === 0;
};

type AccountInfo = {
  id: string;
  accountNumber?: string;
  routingNumber?: string;
  fundingAccountId?: string;
};
type FormErrors = {
  accountNumber?: string;
  routingNumber?: string;
};

// returns object like selectedAccounts but error messages instead of values like
//   { [accountId]: { accountNumber: <error message>, routingNumber: <error message> } }
//   or null if no errors
const validateSelectedAccount = (selectedAccountValidationObj: AccountInfo | null) => {
  const acc = selectedAccountValidationObj;
  const accountErrors: FormErrors = {};
  if (!acc?.routingNumber || acc?.routingNumber.length !== 9) {
    accountErrors.routingNumber = !acc?.routingNumber
      ? 'Routing number is required'
      : 'Routing number must be 9 digits';
  } else if (!validateRoutingNumber(acc?.routingNumber || '')) {
    accountErrors.routingNumber = 'Only U.S.-based accounts are eligible for transfers';
  }
  if (!acc?.accountNumber) {
    accountErrors.accountNumber = 'Account number is required';
  } else if (acc?.accountNumber?.length < 7) {
    accountErrors.accountNumber = 'Please enter a valid account number';
  }

  return Object.keys(accountErrors).length > 0 ? accountErrors : null;
};

const getFinalFourFormatted = (account?: accountsTypes.Account | null, includeName?: boolean) => {
  if (!account) {
    log.warn('Account missing in final4 lookup');
    return '????';
  }
  if (includeName) {
    return `${account?.name} (${accountsUtils.formatFinalFour(account, true)})`;
  }
  const accountSubtitle = `${
    accountsTypes.getAccountTypeNodeByQcsType(account.type, account.subType).displayName
  } ${accountsUtils.formatFinalFour(account)}`;
  return accountSubtitle;
};

//  ======================== MAIN ========================

const OnboardAccountsForTransfersDialog = React.memo((props: OnboardAccountsForTransfersDialogProps) => {
  const isMounted = useRef<boolean>();
  const { dialogId, onClose, startTabIndex, initialInstitutionId, initialAccountId, initiator, overrideOpening } =
    props;
  // startTabIndex is the tab that is the "starting point" after the opening screen. We assume the opening screen will
  // always show unless we specifically override it.
  const dispatch = useDispatch();
  const { classes } = useStyles();

  //  =================== SELECTORS ========================
  const isEmpty = useSelector(fundingAccountSelectors.areNoAccountsEligible);
  const activeIdentity = useSelector(profileSelectors.getIdentityVerified);

  const eligibleTransferAccountsList = useSelector(fundingAccountSelectors.getEligibleTransferAccountsList);

  const hasFundingAccounts = useSelector(fundingAccountSelectors.getHasFundingAccounts);
  const institutionLogins = useSelector(institutionLoginsSelectors.getInstitutionLoginsById);
  const verifiedPendingAndDisabledFundingAccounts = useSelector(
    fundingAccountSelectors.getAllFundingAccountsByAccountId,
  );

  // ================== SELECTIONS ====================
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(initialInstitutionId || null);
  const [selectedAccountValidationObj, setSelectedAccountValidationObj] = useState<AccountInfo | null>(
    initialAccountId ? { id: initialAccountId } : null,
  );
  const [showIncorrectValueError, setShowIncorrectValueError] = useState(false);
  const [fundingAccountToConfirm, _setFundingAccountToConfirm] = useState<FundingAccount | undefined>(undefined);

  // =================== ACCOUNT + INSTITUTION CALLBACKS ============================

  const selectedGroup = useMemo(() => {
    return selectedInstitutionId
      ? eligibleTransferAccountsList.find((group) => group?.institution?.id === selectedInstitutionId)
      : null;
  }, [eligibleTransferAccountsList, selectedInstitutionId]);

  /* Gets the account object based on the selectedAccountValidationObj id */
  const currentAccount = useAppSelector((state) =>
    accountsSelectors.getAccountById(state, selectedAccountValidationObj?.id),
  );
  const currentFundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, currentAccount?.fundingAccountId),
  );
  const a2AInvestments = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'a2AInvestmentAccounts'),
  );

  const isFirstTime = verifiedPendingAndDisabledFundingAccounts?.isEmpty() && !overrideOpening;

  const lastFourDigits = accountsUtils.formatFinalFour(currentAccount).slice(-4);
  const lastFourDontMatch =
    selectedAccountValidationObj?.accountNumber &&
    lastFourDigits &&
    selectedAccountValidationObj?.accountNumber.slice(
      selectedAccountValidationObj?.accountNumber.length - lastFourDigits.length,
    ) !== lastFourDigits;

  //  ===================== STATES ============================
  const [tabIndex, setTabIndex] = useState<onboardA2ADialogIndex>(
    isFirstTime ? onboardA2ADialogIndexEnums.OPENING : startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST,
  );
  const [fieldErrors, setFieldErrors] = useState<FormErrors | null>(null);
  const [agreedToTerms, toggleAgreedToTerms] = useToggle(false);
  const [showLoadingText, setShowLoadingText] = useState(false);

  //  ================= VIEW STATE LOGIC =====================
  //  Views
  const fundingView = useLoadState(fundingAccountSelectors);
  const accountsView = useLoadState(accountsSelectors);
  const profileView = useLoadState(profileSelectors);
  const instLoginView = useLoadState(institutionLoginsSelectors);
  //  We do not include 'institutions' because it only handles retrieving the logo, and that is not crucial

  const mainView = useMemo(() => {
    const showErrorView = tabIndex === onboardA2ADialogIndexEnums.ERROR;

    const showSkeletonView = Boolean(
      loadState.skeletonStates.includes(fundingView) ||
        loadState.skeletonStates.includes(profileView) ||
        loadState.skeletonStates.includes(instLoginView) ||
        loadState.skeletonStates.includes(accountsView) ||
        tabIndex === onboardA2ADialogIndexEnums.LOADING,
    );

    // For some reason, the tryAgainStates must be separated into a separate const in order to prevent a lint error.
    const tryAgainStates = Boolean(
      loadState.tryAgainStates.includes(fundingView) ||
        loadState.tryAgainStates.includes(accountsView) ||
        loadState.tryAgainStates.includes(profileView) ||
        loadState.tryAgainStates.includes(instLoginView),
    );
    const showTryAgainView = Boolean(
      (tryAgainStates ||
        loadState.updateRequiredStates.includes(fundingView) ||
        loadState.updateRequiredStates.includes(accountsView) ||
        loadState.updateRequiredStates.includes(profileView) ||
        loadState.updateRequiredStates.includes(instLoginView)) &&
        !isEmpty &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );

    //  For some reason the noDataState is broken for fundingView, so it will be included in showReadyView and 'isEmpty' will be used to determine if there is no fundingData instead
    const showNoDataView = Boolean(
      (loadState.noDataStates.includes(accountsView) ||
        loadState.noDataStates.includes(profileView) ||
        loadState.noDataStates.includes(instLoginView) ||
        isEmpty) &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );
    const showReadyView = Boolean(
      (loadState.readyStates.includes(fundingView) || loadState.noDataStates.includes(fundingView)) &&
        loadState.readyStates.includes(accountsView) &&
        loadState.readyStates.includes(profileView) &&
        loadState.readyStates.includes(instLoginView) &&
        !isEmpty &&
        tabIndex !== onboardA2ADialogIndexEnums.LOADING,
    );

    let myMainView = '';
    if (showErrorView) {
      myMainView = 'ERROR';
    } else if (showReadyView) {
      myMainView = 'READY';
    } else if (showNoDataView) {
      myMainView = 'EMPTY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    //  OVERRIDE mainView for unique tabIndex exceptions
    // Exception 1: TabIndex === SUCCESS: Technically, there are no available accounts eligible to be setup for transfers in the event
    //              where the user has finished the verification process for ALL available accounts, however, we do not want to show the
    //              empty state screen, we want to show the success screen! We only want to show the empty state screen once the user
    //              has exited the dialog and then later tries to add more accounts (When none are eligible)
    // Exception 2: When the widget first loads in (With tabIndex === ACCT_INFO) there is a small period of time where the account is marked as
    //              ineligible and the funding account does not exist. This is because QCS is still loading changes. During this time, technically
    //              we meet the conditions to show a 0 state, however, as QCS is still loading data, we do not want to show the empty state
    //              screen, so we avoid doing so with this exception.
    if (
      (tabIndex === onboardA2ADialogIndexEnums.SUCCESS ||
        tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT ||
        tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) &&
      myMainView === 'EMPTY'
    ) {
      myMainView = 'READY';
    }
    return myMainView;
  }, [accountsView, fundingView, instLoginView, isEmpty, profileView, tabIndex]);

  /*
  ERROR
  LOADING
  OPENING <- First
  SELECT_INST <- Second
  SELECT_ACCT <- Third
  ACCT_INFO <- Fourth
  CONFIRM <- If Microdeposits
  DEPOSITS_SENT <- Final Option 1
  SUCCESS <- Final Option 2
  */
  // =============== TAB INDEX CONTROL ==================
  const decTabIndex = () => {
    switch (tabIndex) {
      case onboardA2ADialogIndexEnums.SELECT_INST:
        setTabIndex(onboardA2ADialogIndexEnums.OPENING);
        break;
      case onboardA2ADialogIndexEnums.SELECT_ACCT:
        setTabIndex(onboardA2ADialogIndexEnums.SELECT_INST);
        break;
      case onboardA2ADialogIndexEnums.ACCT_INFO:
        setShowIncorrectValueError(false);
        setFieldErrors(null);
        setTabIndex(onboardA2ADialogIndexEnums.SELECT_ACCT);
        break;
      case onboardA2ADialogIndexEnums.CONFIRM:
        setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
        break;
      default:
    }
  };

  const showBackButton =
    tabIndex !== onboardA2ADialogIndexEnums.SUCCESS &&
    tabIndex !== onboardA2ADialogIndexEnums.DEPOSITS_SENT &&
    tabIndex !== onboardA2ADialogIndexEnums.OPENING &&
    tabIndex !== onboardA2ADialogIndexEnums.LOADING &&
    tabIndex !== onboardA2ADialogIndexEnums.ERROR &&
    !(tabIndex === onboardA2ADialogIndexEnums.SELECT_INST && !isEmpty);
  //  ================== EFFECTS ===================

  // track mounted state because of async promise
  useEffect(() => {
    dispatch(profileActions.getProfile());
    dispatch(fundingAccountsActions.getFundingAccounts()); //  Sometimes QCS returns funding accounts as null so this is an added 'get' to be safe? Maybe it will solve it?
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On page open only
  useEffectOnce(() => {
    if (tabIndex === onboardA2ADialogIndexEnums.OPENING) {
      tracker.track(tracker.events.viewTransfersIntroScreen, { initiator: 'account verification' });
    }

    tracker.track(tracker.events.viewAccountSetup, { initiator });
  });

  useEffect(() => {
    // TODO: maybe don't dispatch tracking events in a useEffect?
    if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) {
      tracker.track(tracker.events.enterAccountDetails);
    }
  }, [currentFundingAccount, tabIndex]);

  // =================== CALLBACKS ==================
  const kickoffA2AFlow = useCallback(
    (_e: unknown, startTabIndex: any) => {
      dispatch(
        showOnboardAccountsDialog({
          startTabIndex: startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST,
          initialInstitutionId: selectedGroup?.institution?.id,
          overrideOpening: true,
          initiator,
        }),
      );
    },
    [dispatch, selectedGroup?.institution?.id, initiator],
  );

  const getFundingAccounts = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts());
    dispatch(accountsActions.getAccounts());
    dispatch(profileActions.getProfile());
    dispatch(institutionLoginsActions.getInstitutionLogins());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  }, [dialogId, dispatch, onClose]);

  const showLockoutDialog = useCallback(
    (accountInfo?: FundingAccount, errorCode?: string) => {
      handleClose();
      dispatch(
        showAccountLockoutDialog({
          fundingAccountId: accountInfo?.id,
          errorCode,
        }),
      );
    },
    [dispatch, handleClose],
  );

  const kickoffMicrodepositFlow = useCallback(
    (accountInfo: AccountInfo) => {
      handleClose();
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: accountInfo?.fundingAccountId,
          hideAccountPicker: true,
          initiator: 'onboarding flow',
        }),
      );
    },
    [dispatch, handleClose],
  );

  const handleRequestCreate = useCallback(() => {
    const loginId = (selectedGroup?.eligible || selectedGroup?.others)?.[0]?.institutionLoginId;
    const existingLogin = loginId && institutionLogins.get(loginId);
    handleClose();
    if (existingLogin) {
      dispatch(
        accountDiscoveryActions.doAccountRediscovery(existingLogin, {
          dialogTitle: 'Add account',
          onClose: () => kickoffA2AFlow(null, onboardA2ADialogIndexEnums.SELECT_ACCT),
        }),
      );
    } else {
      dispatch(
        accountDiscoveryActions.doAccountDiscovery({
          initiator: 'manual accounts',
          mode: 'settings accounts link',
        }),
      );
    }
  }, [dispatch, handleClose, institutionLogins, kickoffA2AFlow, selectedGroup]);

  // If there is ANY change in a field done by a user, clear out the error in that field. We will show what the new error is on submit
  const checkErrorDiff = useCallback(
    (newAccount: AccountInfo) => {
      const errors = validateSelectedAccount(newAccount);
      const newFieldErrors: FormErrors = { ...fieldErrors };

      if (errors?.accountNumber !== newFieldErrors?.accountNumber) {
        newFieldErrors.accountNumber = undefined;
      }
      if (errors?.routingNumber !== newFieldErrors?.routingNumber) {
        newFieldErrors.routingNumber = undefined;
      }

      return newFieldErrors;
    },
    [fieldErrors],
  );

  const handleChange = useCallback(
    (newAccount: AccountInfo) => {
      if (!newAccount) {
        setSelectedAccountValidationObj(null);
      } else {
        const updatedErrors = checkErrorDiff(newAccount);
        setFieldErrors(updatedErrors);
        setSelectedAccountValidationObj(newAccount);
      }
    },
    [checkErrorDiff],
  );

  // ============== ON ERROR ========================
  const onError = useCallback(
    (error: any) => {
      tracker.track(tracker.events.accountSetupError, {
        error: error?.response?.data?.errors[0]?.detail || error?.response?.data?.errors[0]?.title,
      });
      if (
        error?.response?.data?.errorCode === 'IFS-AOV-0006' ||
        error?.response?.data?.errorCode === 'IFS-AOV-0001' ||
        error?.response?.data?.errorCode === 'IFS-AOV-0002' ||
        error?.response?.data?.errorCode === 'IFS-AOV-0003' ||
        error?.response?.data?.errorCode === 'IFS-AOV-0004' ||
        error?.response?.data?.errorCode === 'IFS-AOV-0005'
      ) {
        setShowIncorrectValueError(true);
        setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
        if (selectedAccountValidationObj?.id) {
          if (agreedToTerms) {
            toggleAgreedToTerms();
          }
          handleChange({
            id: selectedAccountValidationObj.id,
            accountNumber: undefined,
            routingNumber: undefined,
          });
        }
      } else {
        showLockoutDialog(undefined, error?.response?.data?.errorCode);
      }
    },
    [agreedToTerms, handleChange, selectedAccountValidationObj, showLockoutDialog, toggleAgreedToTerms],
  );

  const navigateFromSubmission = useCallback(
    (resp: FundingAccount) => {
      if (isMounted.current === true) {
        setShowLoadingText(false);
        getFundingAccounts();
        toggleAgreedToTerms();
        if (resp?.status === statusEnum.VERIFIED) {
          tracker.track(tracker.events.accountSetupCompleted, { result: 'account enabled' });
          setTabIndex(onboardA2ADialogIndexEnums.READY_TO_TRANSFER);
        } else if (
          resp?.status === statusEnum.DISABLED ||
          resp?.status === statusEnum.FAILED ||
          resp?.status === statusEnum.UNVERIFIED
        ) {
          tracker.track(tracker.events.accountSetupError, {
            error: 'Failed to deposit amounts to account',
          });
          showLockoutDialog(resp);
        } else if (
          (resp?.status === statusEnum.VERIFICATION_IN_PROGRESS || resp?.status === statusEnum.SUBMITTED) &&
          resp?.subStatus === subStatusEnum.DEPOSIT_REQUIRED
        ) {
          tracker.track(tracker.events.accountSetupCompleted, { result: 'micro deposit sent' });
          setTabIndex(onboardA2ADialogIndexEnums.DEPOSITS_SENT);
        } else if (
          (resp?.status === statusEnum.VERIFICATION_IN_PROGRESS || resp?.status === statusEnum.SUBMITTED) &&
          resp?.subStatus === subStatusEnum.NO_OP
        ) {
          //  tracker.track(tracker.events.accountSetupCompleted, { result: 'account enabled' });
          setTabIndex(onboardA2ADialogIndexEnums.SUCCESS);
        } /* else if (resp?.status === statusEnum.SUBMITTED && resp?.subStatus === subStatusEnum.DEPOSIT_REQUIRED) {
        setFundingAccountToConfirm(resp);
        setTabIndex(onboardA2ADialogIndexEnums.CONFIRM);
      } */
      }
    },
    [getFundingAccounts, showLockoutDialog, toggleAgreedToTerms],
  );

  const confirmFundingAccount = useCallback(() => {
    setTabIndex(onboardA2ADialogIndexEnums.LOADING);
    actionHelpers
      .bindPromiseAction(
        dispatch,
        fundingAccountsActions.confirmFundingAccount,
      )(fundingAccountToConfirm)
      .then((resp: any) => {
        navigateFromSubmission(resp);
      })
      .catch((error) => {
        setTimeout(() => {
          setShowLoadingText(false);
          onError(error);
        }, 3000);
      });
  }, [dispatch, fundingAccountToConfirm, navigateFromSubmission, onError]);

  const handleSubmitForm = useCallback(() => {
    setTabIndex(onboardA2ADialogIndexEnums.LOADING);
    setShowLoadingText(true);
    if (!currentAccount) return;

    const isInvestment = fundingAccountUtils.isAccountA2aDepositOnly(currentAccount);

    //  Map MONEY_MARKET to SAVINGS. Map CASH_MANAGEMENT and INVESTMENT types to CHECKING. We want to allow MONEY_MARKET/CASH_MANAGEMENT accounts.
    //  QCS FundingAccounts cannot handle the subtype, so we change it to SAVINGS/CHECKING.
    //  I am not sure why it is not done on the QCS end, but apparently it is difficult, so it will be done here for now.
    let bankType = currentAccount.subType;
    if (bankType === 'CASH_MANAGEMENT' || currentAccount.type === 'INVESTMENT') {
      bankType = 'CHECKING';
    } else if (bankType === 'MONEY_MARKET') {
      bankType = 'SAVINGS';
    }

    const payloadFundingAccount = mkFundingAccount({
      routingNumber: selectedAccountValidationObj?.routingNumber,
      accountNumber: selectedAccountValidationObj?.accountNumber,
      accountType: bankType as FundingAccountType,
      ownerType: FundingAccountOwnerTypeEnum.PERSONAL,
      nickname: currentAccount.name,
      linkedAccountId: currentAccount.id ?? undefined,
      destinationOnly: isInvestment,
    });

    const payload = {
      fundingAccount: payloadFundingAccount,
      userSetAccountType: currentAccount.type,
    };

    actionHelpers
      .bindPromiseAction(
        dispatch,
        fundingAccountsActions.createFundingAccount,
      )(payload)
      .then((resp: any) => {
        navigateFromSubmission(resp);
      })
      .catch((error) => {
        setTimeout(() => {
          setShowLoadingText(false);
          onError(error);
        }, 3000);
      });
  }, [
    currentAccount,
    selectedAccountValidationObj?.routingNumber,
    selectedAccountValidationObj?.accountNumber,
    dispatch,
    navigateFromSubmission,
    onError,
  ]);

  const openWarningDialog = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'numbers-dont-match',
          type: DIALOG_CONFIRMATION,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              size: 'xs',
              title: (
                <div className={classes.titleWrapper}>
                  <WarningRoundedIcon className={classes.warningIcon} />
                  <Typography variant='titleMedium'>Account number mismatch</Typography>
                </div>
              ),
              content: (
                <div>
                  <Typography variant='titleRegular' className={classes.marginBottomTwo}>
                    The last four digits of the account number you entered don’t match the last four digits of your
                    connected account (
                    <span className={classes.bold}>{accountsUtils.formatFinalFour(currentAccount)}</span>).
                  </Typography>
                  <Typography variant='titleRegular'>
                    Please check that the account number entered matches the account you’re trying to enable before
                    continuing.
                  </Typography>
                </div>
              ),
              confirmLabel: 'Continue',
              denyLabel: 'Cancel',
              confirmAction: handleSubmitForm,
            }),
          ),
        }),
      ),
    );
  }, [classes, currentAccount, dispatch, handleSubmitForm]);

  const handleErrorsOnContinue = useCallback(() => {
    if (!currentAccount) return;
    const errors = validateSelectedAccount(selectedAccountValidationObj);
    if (errors) {
      setFieldErrors(errors);
    } else {
      setFieldErrors(null);
      if (lastFourDontMatch) {
        openWarningDialog();
      } else {
        handleSubmitForm();
      }
    }
  }, [currentAccount, selectedAccountValidationObj, lastFourDontMatch, openWarningDialog, handleSubmitForm]);

  const handleContinue = useCallback(
    (obj: any) => {
      switch (tabIndex) {
        case onboardA2ADialogIndexEnums.OPENING:
          setTabIndex(startTabIndex || onboardA2ADialogIndexEnums.SELECT_INST);
          break;
        case onboardA2ADialogIndexEnums.SELECT_INST:
          tracker.track(tracker.events.institutionSelect, {
            number: eligibleTransferAccountsList?.size,
            institution_name: obj?.institution?.name,
          });
          setSelectedInstitutionId(obj?.institution?.id);
          setTabIndex(onboardA2ADialogIndexEnums.SELECT_ACCT);
          break;
        case onboardA2ADialogIndexEnums.SELECT_ACCT:
          tracker.track(tracker.events.accountSelect, {
            number: selectedGroup?.eligible?.length,
            status: 'eligible',
            type: accountsTypes?.getAccountTypeNodeByQcsType(obj?.type, obj?.subType)?.displayName.toLowerCase(),
          });
          setTabIndex(onboardA2ADialogIndexEnums.ACCT_INFO);
          break;
        case onboardA2ADialogIndexEnums.ACCT_INFO:
          handleErrorsOnContinue();
          break;
        case onboardA2ADialogIndexEnums.CONFIRM:
          confirmFundingAccount();
          break;
        case onboardA2ADialogIndexEnums.DEPOSITS_SENT:
        case onboardA2ADialogIndexEnums.SUCCESS:
          handleClose();
          break;
        default:
          setTabIndex(onboardA2ADialogIndexEnums.ERROR);
          break;
      }
    },
    [
      tabIndex,
      startTabIndex,
      eligibleTransferAccountsList?.size,
      selectedGroup?.eligible?.length,
      handleErrorsOnContinue,
      confirmFundingAccount,
      handleClose,
    ],
  );

  const handleOpenAccountDiscovery = useCallback(() => {
    dispatch(accountDiscoveryActions.doAccountDiscovery({ initiator: 'a2a no accounts' }));
    handleClose();
  }, [dispatch, handleClose]);

  const kickoffTransferFlow = (_e?: any) => {
    handleClose();
    dispatch(
      showInitiateTransferDialog({
        initiator: 'onboarding accounts success screen',
      }),
    );
  };

  // =============== DERIVED VALUES =================
  let dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.ERROR) dialogTitle = 'Enabling transfers failed';
  if (tabIndex === onboardA2ADialogIndexEnums.SELECT_INST) dialogTitle = 'Select institution';
  if (tabIndex === onboardA2ADialogIndexEnums.SELECT_ACCT) dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.LOADING) dialogTitle = 'Set up account transfers';
  if (tabIndex === onboardA2ADialogIndexEnums.CONFIRM) dialogTitle = 'Review account details';
  if (tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT) dialogTitle = 'Verification deposits sent';
  if (tabIndex === onboardA2ADialogIndexEnums.SUCCESS) dialogTitle = 'Verification in progress';
  if (tabIndex === onboardA2ADialogIndexEnums.READY_TO_TRANSFER) dialogTitle = 'Account verified';

  let disableContinue = false;

  // verify that we have at least 1 valid accounts and no fieldErrors (FieldErrors should be allowed since they are only set by clicking continue, so cannot be unset unless continue is clickable)
  if (
    tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO &&
    ((!hasFundingAccounts && !selectedAccountValidationObj) || !agreedToTerms)
  ) {
    disableContinue = true;
  }
  if (tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && !selectedAccountValidationObj) {
    disableContinue = true;
  }
  if (!activeIdentity) {
    disableContinue = true;
  }

  let continueText = 'continue';
  if (tabIndex === onboardA2ADialogIndexEnums.OPENING) continueText = 'get started';
  if (tabIndex === onboardA2ADialogIndexEnums.CONFIRM) continueText = 'Send verification deposits';
  if (tabIndex === onboardA2ADialogIndexEnums.SUCCESS || tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT)
    continueText = 'done';

  // =============== RENDER FUNCTIONS =================

  const showEmptyView = useMemo(() => {
    let secondaryText =
      "To transfer money in Simplifi, you'll first need to connect a U.S. based, personal checking or savings account.";

    if (loadState.noDataStates.includes(profileView)) {
      secondaryText += ' You must have a validate your identity to transfer money between accounts';
    }

    return (
      <ZeroStateView
        className={classes.errorTab}
        id='zero-state'
        title='No eligible accounts added yet'
        description={secondaryText}
        qIllustration='planeCrash'
        button={
          <Button onClick={handleOpenAccountDiscovery} variant='contained' startIcon={<AddCircleIcon />}>
            account
          </Button>
        }
      />
    );
  }, [classes, handleOpenAccountDiscovery, profileView]);

  /* After clicking on an institution, this renders the list of accounts shown to the user */
  const renderAccountListForInstitution = useMemo(() => {
    if (!selectedGroup) {
      return {
        eligibleAccounts: [],
        ineligibleAccounts: [],
      };
    }

    const eligibleAccounts =
      selectedGroup?.eligible?.map((account, index) => (
        <TransferAccountRow
          className={classes.transferAccountRow}
          key={account.id}
          account={account}
          index={index}
          kickoffMicrodepositFlow={kickoffMicrodepositFlow}
          handleContinue={() => handleContinue(account)}
          handleChange={handleChange}
          formatFinalFour={getFinalFourFormatted}
        />
      )) || [];

    if (selectedGroup?.enabled?.length) {
      selectedGroup?.enabled.forEach((account, index) => {
        eligibleAccounts.push(
          <TransferAccountRow
            className={classes.transferAccountRow}
            key={account.id}
            account={account}
            index={index}
            kickoffMicrodepositFlow={kickoffMicrodepositFlow}
            handleContinue={() => handleContinue(account)}
            handleChange={handleChange}
            formatFinalFour={getFinalFourFormatted}
          />,
        );
      });
    }

    const ineligibleAccounts =
      selectedGroup?.others?.map((account, index) => (
        <TransferAccountRow
          className={classes.transferAccountRow}
          key={account.id}
          account={account}
          index={index}
          kickoffMicrodepositFlow={kickoffMicrodepositFlow}
          handleContinue={handleContinue}
          handleChange={handleChange}
          formatFinalFour={getFinalFourFormatted}
        />
      )) || [];

    const accountObj = {
      eligibleAccounts,
      ineligibleAccounts,
    };

    return accountObj;
  }, [classes, handleChange, handleContinue, selectedGroup, kickoffMicrodepositFlow]);

  const getInstitutionLoginInErrorByInstId = (institutionId: string) =>
    institutionLogins.find(
      (login: any) =>
        login.institutionId === institutionId &&
        login?.aggregators?.first()?.aggStatus !== 'OK' &&
        login?.aggregators?.first()?.aggStatus !== null,
    ) || null;

  return (
    <StdDialog
      dialogId={dialogId}
      disableBackdropClick={tabIndex === onboardA2ADialogIndexEnums.LOADING}
      onBack={() => {
        if (agreedToTerms && tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO) toggleAgreedToTerms();
        decTabIndex();
      }}
      onClose={handleClose}
      sharedcomponentid='DIALOG_TYPE_SELECT_ACCOUNTS_FOR_TRANSFERS'
      showBackButton={showBackButton}
      showCloseButton={tabIndex !== onboardA2ADialogIndexEnums.LOADING}
      size='md'
      title={dialogTitle}
    >
      <StdDialogContent dialogId={dialogId}>
        {
          // startup page that only appears if it is the users first time
          tabIndex === onboardA2ADialogIndexEnums.OPENING && mainView === 'READY' && <SetupPage />
        }
        {
          // select institution
          tabIndex === onboardA2ADialogIndexEnums.SELECT_INST && mainView === 'READY' && (
            <div className={classes.viewOneWrapper}>
              <Dump obj={eligibleTransferAccountsList} />
              <div className={classes.moneyColumn}>
                <Box p={3}>
                  <QIllustration name='FiBuilding' size='lg' />
                </Box>
              </div>
              <div className={classes.institutionSelectWrapper}>
                <Typography variant='bodyRegular'>
                  Select the financial institution you want to enable transfers for.
                </Typography>
                <Typography className={classes.marginBottom} color='text.secondary' variant='paragraphRegular'>
                  {a2AInvestments
                    ? 'Only U.S.-based, personal checking, savings and investment accounts are eligible.'
                    : 'Only U.S.-based, personal checking and savings accounts are eligible.'}
                </Typography>
                {eligibleTransferAccountsList.map((institutionGroup, index) => (
                  <TransferInstitutionRow
                    key={institutionGroup?.institution?.id}
                    index={index}
                    handleContinue={handleContinue}
                    institutionGroup={institutionGroup}
                    institutionLogin={getInstitutionLoginInErrorByInstId(
                      institutionGroup?.institution?.get('id') || '',
                    )}
                  />
                ))}
              </div>
            </div>
          )
        }
        {
          // select account
          tabIndex === onboardA2ADialogIndexEnums.SELECT_ACCT && mainView === 'READY' && (
            <div className={classes.viewOneWrapper}>
              <div className={classes.instituteIconWrapper}>
                <InstitutionIcon institution={selectedGroup?.institution || null} />
              </div>
              <div className={classes.institutionSelectWrapper}>
                <Typography variant='headlineMedium' className={classes.accountEligibleTitle}>
                  {selectedGroup?.institution?.name}
                </Typography>
                <Typography variant='paragraphRegular' className={classes.accountEligibleNumber}>
                  {`${selectedGroup?.eligible?.length} account${
                    selectedGroup?.eligible?.length === 1 ? '' : 's'
                  } eligible`}
                </Typography>
                <InstitutionErrorBanner
                  institutionLogin={getInstitutionLoginInErrorByInstId(selectedGroup?.institution?.get('id') || '')}
                  location='onboarding for transfer flow'
                  className={classes.errorBanner}
                />
                {Boolean(renderAccountListForInstitution.eligibleAccounts.length) && (
                  <Typography variant='bodyRegular' className={classes.accountEligibleText}>
                    {' '}
                    Accounts eligible for transfers{' '}
                  </Typography>
                )}

                {renderAccountListForInstitution.eligibleAccounts}

                <div className={classes.buttonWrapper}>
                  <QButton onClick={handleRequestCreate} className={classes.endButton} startIcon={<AddCircleIcon />}>
                    account
                  </QButton>
                </div>

                {Boolean(renderAccountListForInstitution.ineligibleAccounts.length) && (
                  <Typography variant='bodyRegular' className={classes.accountEligibleText}>
                    {' '}
                    All other accounts{' '}
                  </Typography>
                )}
                {renderAccountListForInstitution.ineligibleAccounts}
              </div>
            </div>
          )
        }
        {tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO && mainView === 'READY' && (
          <div className={classes.viewOneWrapper}>
            <div className={classes.instituteIconWrapper}>
              <InstitutionIcon institution={selectedGroup?.institution || null} />
            </div>
            <div className={classes.institutionSelectWrapper}>
              <Typography variant='titleMedium' className={classes.accountEligibleTitle}>
                {currentAccount?.name ?? 'Unknown Account'}
              </Typography>
              <Typography variant='paragraphRegular' className={classes.accountEligibleNumber}>
                {currentAccount ? getFinalFourFormatted(currentAccount, false) : 'Unknown Account Type'}
              </Typography>
              {showIncorrectValueError ? (
                <div className={classes.errorTextWrap}>
                  <ErrorIcon className={classes.errorIcon} />
                  <div>
                    <Typography variant='paragraphMedium'>{'Incorrect routing or account number'}</Typography>
                    <Typography variant='paragraphRegular'>
                      {'Double-check your account details and try again.'}
                    </Typography>
                  </div>
                </div>
              ) : null}
              <div className={classes.accountFormWrapper}>
                <Typography variant='bodyRegular' className={classes.accountDetailText}>
                  {'Enter your account details to set up transfers.'}
                </Typography>

                <SelectAccountForm
                  key={`select-account-${currentAccount?.id}`}
                  account={selectedAccountValidationObj}
                  accountNumber={selectedAccountValidationObj?.accountNumber}
                  routingNumber={selectedAccountValidationObj?.routingNumber}
                  onChange={handleChange}
                  fieldErrors={fieldErrors}
                  institution={selectedGroup?.institution}
                  agreedToTerms={agreedToTerms}
                  toggleAgreedToTerms={toggleAgreedToTerms}
                />
              </div>
            </div>
          </div>
        )}
        {
          // Confirm the information you entered is correct screen (DWOLLA)
          tabIndex === onboardA2ADialogIndexEnums.CONFIRM && mainView === 'READY' && (
            <Box p={2} pl={3} pr={3}>
              <Typography variant='headlineRegular'>You&apos;re almost done!</Typography>
              <Typography variant='bodyRegular'>
                Two small amounts (less than $1 each) will be deposited to verify your account.
              </Typography>
              <Divider className={classes.divider} />
              <Typography color='textSecondary' variant='titleRegular'>
                Verify Details
              </Typography>
              <div className={classes.detailsWrapper}>
                <div className={classes.accountWrapper}>
                  <Typography variant='captionRegular' className={classes.secondaryText}>
                    {' '}
                    Account{' '}
                  </Typography>
                  <Typography variant='bodyRegular'>{getFinalFourFormatted(currentAccount, true)}</Typography>
                </div>
                <div className={classes.insideWrapper}>
                  <div className={classes.accountNumberWrapper}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      {' '}
                      Account number{' '}
                    </Typography>
                    <Typography variant='bodyRegular'>{selectedAccountValidationObj?.accountNumber}</Typography>
                  </div>
                  <div className={classes.accountWrapper}>
                    <Typography variant='captionRegular' className={classes.secondaryText}>
                      {' '}
                      Routing number{' '}
                    </Typography>
                    <Typography variant='bodyRegular'>{selectedAccountValidationObj?.routingNumber}</Typography>
                  </div>
                </div>
              </div>
            </Box>
          )
        }
        {(tabIndex === onboardA2ADialogIndexEnums.SUCCESS || tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT) &&
          mainView === 'READY' && (
            <Box
              pt={2}
              pb={2}
              pl={4}
              pr={4}
              display='flex'
              flexDirection='column'
              alignItems='center'
              textAlign='center'
            >
              {tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT ? (
                <QIllustration name='verificationDeposits' size='lg' />
              ) : (
                <QIllustration name='thumbsUp' size='lg' />
              )}
              <Typography variant='displayRegular' className={classes.pendingTitle}>
                {tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT
                  ? 'Verification deposits on the way!'
                  : 'Verification is in progress!'}
              </Typography>
              <Typography variant='bodyRegular' className={classes.secondaryTextItem}>
                {tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT
                  ? 'Two small amounts will be deposited into this account within a day or two. You’ll then be notified on how to complete account verification.'
                  : 'We are in the process of verifying your account. You will be notified as soon as it is complete'}
              </Typography>
            </Box>
          )}
        {
          // If the funding account already exists and is already verified and then reconnects itself to the login account (DWOLLA) OR if the fundingAccount is an instant set up account (INTUIT WIDGET)
          tabIndex === onboardA2ADialogIndexEnums.READY_TO_TRANSFER && mainView === 'READY' && (
            <Box className={classes.successTab}>
              <QIllustration name='thumbsUp' size='lg' />
              <Box mt={3} mb={3}>
                <Typography variant='displayRegular'>Account ready for transfers!</Typography>
              </Box>
              <Typography variant='bodyRegular' color='textSecondary'>
                Transfer money now, or use the <b>Transfer Money</b> button at the top of this account on the
                Transactions page.
              </Typography>
            </Box>
          )
        }
        {(mainView === 'ERROR' || tabIndex === onboardA2ADialogIndexEnums.ERROR) && (
          <Box className={classes.errorTab}>
            <ZeroStateView
              id='microdeposit-failure'
              title={'Uh-oh!'}
              description={'We hit a snag. Please try again.'}
              qIllustration='planeCrash'
            />
          </Box>
        )}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            {' '}
            <LoadingView size={100} />
            {showLoadingText ? (
              <>
                <Typography variant='headlineRegular'>Verifying your account details</Typography>
                <Typography variant='bodyRegular' className={classes.loadingSubText}>
                  {"This won't take long!"}
                </Typography>
              </>
            ) : null}
          </Box>
        )}
        {mainView === 'FAILED' && (
          <ZeroStateView
            className={classes.errorTab}
            button={
              <Button id={'try-again-button'} variant='outlined' onClick={getFundingAccounts}>
                Try again
              </Button>
            }
            id='failed-to-fetch'
            title='Unable to fetch data'
            description="We can't fetch your data from the server. Please try again"
            qIllustration='planeCrash'
          />
        )}
        {mainView === 'EMPTY' && showEmptyView}
      </StdDialogContent>
      {mainView === 'READY' &&
        (tabIndex === onboardA2ADialogIndexEnums.OPENING ||
          tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO ||
          tabIndex === onboardA2ADialogIndexEnums.CONFIRM) && (
          <StdDialogActions
            primary={{
              disabled: disableContinue,
              children: continueText,
              onClick: handleContinue,
              variant: 'contained',
            }}
            tertiaryNode={
              tabIndex === onboardA2ADialogIndexEnums.ACCT_INFO ? (
                <Typography variant='captionRegular' className={classes.footer} component='div'>
                  <LockIcon className={classes.subLock} />
                  Safe and secure with Quicken
                </Typography>
              ) : null
            }
          />
        )}
      {mainView === 'READY' && tabIndex === onboardA2ADialogIndexEnums.READY_TO_TRANSFER && (
        <StdDialogActions
          primary={{
            children: 'Transfer Funds',
            onClick: kickoffTransferFlow,
            variant: 'contained',
          }}
          secondary={{
            children: 'Close',
            onClick: handleClose,
            variant: 'outlined',
          }}
          tertiaryNode={
            <QButton
              variant='text'
              onClick={() => {
                setSelectedInstitutionId(null);
                setSelectedAccountValidationObj(null);
                setTabIndex(onboardA2ADialogIndexEnums.SELECT_INST);
              }}
            >
              Set up another account
            </QButton>
          }
        />
      )}
      {mainView === 'READY' &&
        (tabIndex === onboardA2ADialogIndexEnums.SUCCESS || tabIndex === onboardA2ADialogIndexEnums.DEPOSITS_SENT) && (
          <StdDialogActions
            primary={{
              disabled: disableContinue,
              children: continueText,
              onClick: handleContinue,
              variant: 'contained',
            }}
            secondary={{
              children: 'set up another account',
              variant: 'outlined',
              onClick: () => {
                setSelectedInstitutionId(null);
                setSelectedAccountValidationObj(null);
                setTabIndex(onboardA2ADialogIndexEnums.SELECT_INST);
              },
            }}
          />
        )}
      {mainView === 'ERROR' && (
        <StdDialogActions
          primary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
    </StdDialog>
  );
});

OnboardAccountsForTransfersDialog.displayName = 'OnboardAccountsForTransfersDialog';

export default OnboardAccountsForTransfersDialog;
